import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { portalApi, portalError } from "./portalApi";
import { Btn, Empty } from "../components/UI";
import { Send, ArrowLeft, ShieldCheck, Mail, Check, CheckCheck } from "lucide-react";

function ReceiptTicks({ msg }) {
  if (!msg) return null;
  const read = !!msg.read_at;
  const delivered = !!msg.delivered_at;
  const title = read
    ? `Read ${dayjs(msg.read_at).format("MMM D, h:mm A")}`
    : delivered
      ? `Delivered ${dayjs(msg.delivered_at).format("MMM D, h:mm A")}`
      : "Sent";
  return (
    <span data-testid={`portal-receipt-${msg.id}`} data-state={read ? "read" : delivered ? "delivered" : "sent"} title={title}
      className="inline-flex items-center text-white/80">
      {read || delivered ? (
        <CheckCheck size={13} className={read ? "text-[#4FC3F7]" : "text-white/70"} />
      ) : (
        <Check size={13} className="text-white/70" />
      )}
    </span>
  );
}

export default function PortalMessages() {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollBoxRef = useRef(null);

  const loadThreads = () => portalApi.get("/portal/messages/threads")
    .then((r) => setThreads(r.data)).catch((e) => toast.error(portalError(e)));

  const scrollToLatest = () => {
    const box = scrollBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  };

  const openThread = (tid) => {
    setActiveId(tid);
    portalApi.get(`/portal/messages/threads/${tid}`).then((r) => {
      setActiveThread(r.data.thread);
      setMessages(r.data.messages);
      setThreads((ts) => ts.map((t) => (t.id === tid ? { ...t, unread_for_client: false } : t)));
      setTimeout(scrollToLatest, 40);
    }).catch((e) => toast.error(portalError(e)));
  };

  useEffect(() => { loadThreads(); }, []);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await portalApi.post(`/portal/messages/threads/${activeId}/messages`, { body: reply.trim() });
      setReply("");
      openThread(activeId); loadThreads();
    } catch (err) { toast.error(portalError(err)); }
    finally { setSending(false); }
  };

  if (activeId && activeThread) {
    return (
      <div data-testid="portal-messages-thread" className="max-w-3xl mx-auto space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button data-testid="portal-messages-back" onClick={() => { setActiveId(null); setActiveThread(null); setMessages([]); }}
            className="flex items-center gap-1.5 text-xs font-bold text-[#7B6157] hover:text-[#4E342E]">
            <ArrowLeft size={13} /> Back to inbox
          </button>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B] flex items-center gap-1">
            <ShieldCheck size={12} /> HIPAA Secure
          </div>
        </div>
        <div className="rounded-2xl bg-white shadow-sm border border-[#EFEBE0] p-4">
          <div className="font-heading text-sm font-bold text-[#3E2723]">{activeThread.subject}</div>
          <div className="text-[11px] text-[#7B6157]">{activeThread.message_type} · started {dayjs(activeThread.created_at).format("MM/DD/YYYY h:mm A")}</div>
          <div ref={scrollBoxRef} className="mt-4 space-y-3 max-h-[480px] overflow-y-auto pr-1">
            {messages.map((m) => {
              const mine = m.from_side === "client";
              return (
                <div key={m.id} data-testid={`portal-message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#3E2723]"}`}>
                    <div className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${mine ? "text-white/70" : "text-[#7B6157]"}`}>
                      {m.from_name} · {dayjs(m.created_at).format("MMM D h:mm A")}
                    </div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    {mine && (
                      <div className="mt-1 flex justify-end items-center gap-1 text-[10px] text-white/70">
                        <ReceiptTicks msg={m} />
                        <span>{m.read_at ? `Read ${dayjs(m.read_at).format("h:mm A")}` : m.delivered_at ? "Delivered" : "Sent"}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={submitReply} className="mt-4 border-t border-[#F1EEE7] pt-3 flex items-end gap-2">
            <textarea data-testid="portal-messages-reply-input" rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder="Type your reply. Please do not share verification codes or passwords."
              className="flex-1 rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
            <Btn data-testid="portal-messages-send-btn" type="submit" disabled={sending}>
              <Send size={14} /> {sending ? "Sending…" : "Send"}
            </Btn>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="portal-messages" className="max-w-3xl mx-auto space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg font-bold text-[#3E2723]">Messages</h1>
          <div className="text-xs text-[#7B6157]">Secure conversations with your care team. Messages stay inside this portal, never in email.</div>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B] flex items-center gap-1">
          <ShieldCheck size={12} /> HIPAA Secure
        </div>
      </div>
      {threads.length === 0 ? (
        <Empty text="You have no messages yet. Your care team will reach out here when needed." />
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <button key={t.id} type="button" data-testid={`portal-thread-${t.id}`} onClick={() => openThread(t.id)}
              className="w-full text-left rounded-xl border border-[#EFEBE0] bg-white p-3 hover:bg-[#FDFCF8] transition-colors">
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-[#7B6157] shrink-0" />
                <span className="font-semibold text-sm text-[#3E2723] truncate">{t.subject}</span>
                {t.unread_for_client && <span data-testid={`portal-thread-unread-${t.id}`} className="rounded-full bg-[#C62828] px-2 py-0.5 text-[9px] font-bold uppercase text-white">Unread</span>}
              </div>
              <div className="mt-0.5 text-[11px] text-[#7B6157]">
                {t.message_type} · last message {dayjs(t.last_message_at).format("MM/DD/YYYY h:mm A")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
